import React, { FC } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import { lighten } from "@mui/material/styles";
import { useState } from "react";
import theme, { colors } from "../../theme";
import { PricingFooter } from "./PricingFooter";
import { Material } from "./types";
import CloseIcon from "@mui/icons-material/Cancel";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { CurrencyWithPrice } from "../CurrencyWithPrice";
import { useMaterials } from "../../hooks/useMaterials";
import { IDesign } from "../../types";
import StarIcon from "@mui/icons-material/Star";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const buttonBarHeight = "60px";

export type IMaterialChooserTranslations = {
  chooseYourMaterial: string;
  wePrintOn: string;
  cancel: string;
  continue: string;
};

export type MaterialChooserProps = {
  locale: string;
  materials: Material[];
  selectedMaterialSku?: string | null;
  design: IDesign;
  showPricesIncTax: boolean;
  price: any;
  materialSku: string;

  onChange?: (materialSku: string) => void;
  onChangeConfirmed: (materialSku: string) => void;
  onCancel: () => void;
  onClickInfo?: (materialSku: string) => void;

  translations: IMaterialChooserTranslations;
};

const TooltipBox = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.colors.greenLightest,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.colors.greenLightest,
    color: theme.colors.coolBlueDark,
    maxWidth: 225,
    fontSize: theme.typography.pxToRem(12),
    border: "1px solid #dadde9",
    borderRadius: 10,
    padding: 10,
  },
}));

export const MaterialChooser: FC<MaterialChooserProps> = (
  props: MaterialChooserProps
) => {
  const [selectedMaterialSku, setSelectedMaterialSku] = useState(
    props.materialSku
  );

  const { otherMaterialCosts } = useMaterials(
    props.materials,
    props.design,
    props.showPricesIncTax,
    true
  );

  return (
    <>
      <Box
        sx={{
          overflow: "auto",
          position: "sticky",
          bottom: 0,
          height: `calc(100vh - ${buttonBarHeight})`,
          maxHeight: {
            md: 600,
          },
        }}
      >
        <Box>
          <IconButton
            title={props.translations.cancel}
            sx={{
              position: "absolute",
              top: 0,
              right: 0.5,
              color: (theme) => theme.colors.grey,

              display: { md: "none" },
            }}
            onClick={() => props.onCancel()}
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ marginLeft: 4, marginTop: 2 }} variant="h5">
            {props.translations.chooseYourMaterial}
          </Typography>
          <Box display="flex" justifyContent="center">
            <List sx={{ margin: 2, marginTop: 0, width: "100%" }}>
              {props.materials.map((material, idx) => {
                const extraCost = otherMaterialCosts[material.sku];
                const priceDiff =
                  material.sku === props.design.materialSku
                    ? 0
                    : extraCost?.price - props.price.price;

                return (
                  <ListItem
                    key={material.sku}
                    sx={{
                      borderWidth: 1,
                      borderStyle: "solid",
                      backgroundColor:
                        material.sku === selectedMaterialSku
                          ? lighten(colors.coolBlueLight, 0.25)
                          : "#fff",
                      borderRadius: "9px",
                      marginLeft: 0,
                      marginBottom: 1,
                      paddingTop: 2,
                      paddingBottom: 2,
                      minHeight: priceDiff !== 0 ? 125 : undefined,
                      cursor: "pointer",
                      borderColor: "#fff",
                      "&:hover": {
                        borderColor: colors.coolBlueLight,
                      },
                      position: "relative",
                      alignItems: "start",
                    }}
                    onClick={() => {
                      if (props.onChange) {
                        props.onChange(material.sku);
                      }

                      setSelectedMaterialSku(material.sku);
                    }}
                  >
                    {props.onClickInfo ? (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 0,
                        }}
                      >
                        <IconButton
                          sx={{ color: colors.greyLight, zIndex: 9999 }}
                          onClick={(ev) => {
                            ev.preventDefault();
                            ev.stopPropagation();

                            if (props.onClickInfo) {
                              props.onClickInfo(material.sku);
                            }
                          }}
                        >
                          <InfoIcon />
                        </IconButton>
                      </Box>
                    ) : null}

                    {material.bestseller ? (
                      <Box sx={{ position: "absolute", right: 8, top: 8 }}>
                        <Box
                          sx={{
                            px: "10px",
                            py: "4px",
                            bgcolor: "#FFC000",
                            borderRadius: "20px",
                            color: theme.colors.black,
                            fontSize: "10px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <StarIcon
                            sx={{
                              fontSize: "10px",
                              colors: theme.colors.black,
                            }}
                          />
                          <Box component="span" sx={{ ml: "2px" }}>
                            {material.bestseller}
                          </Box>
                        </Box>
                      </Box>
                    ) : null}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 5,
                        right: 5,
                        borderRadius: 9,
                        backgroundColor: colors.greenSuperLight,
                        padding: 1,
                      }}
                    >
                      <Typography variant="body2" component="span">
                        {priceDiff > 0 ? "+" : null}
                        <CurrencyWithPrice
                          price={priceDiff}
                          currencyCode={props.price.currencyCode}
                          locale={props.locale}
                        />
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex" }}>
                      <Box sx={{ width: "70px" }}>
                        <img
                          title={material.name}
                          alt={`${material.name} icon`}
                          loading={idx < 3 ? "eager" : "lazy"}
                          width="70"
                          height="70"
                          src={material.imageSrc}
                        />
                      </Box>
                      <Box sx={{ marginLeft: 2 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {material.name}
                          <TooltipBox
                            title={
                              <React.Fragment>
                                <Typography
                                  color="inherit"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {material.name}
                                </Typography>
                                <Typography
                                  component="p"
                                  sx={{ fontSize: "12px" }}
                                >
                                  {material.description}
                                </Typography>
                              </React.Fragment>
                            }
                          >
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: 16,
                                color: theme.colors.coolBlue,
                                cursor: "pointer",
                                ml: "5px",
                                position: "relative",
                                top: "2.5px",
                              }}
                            />
                          </TooltipBox>
                        </Typography>
                        <Typography variant="body2" sx={{ marginTop: 1 }}>
                          {material.description}
                        </Typography>
                      </Box>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Box>
      </Box>
      <PricingFooter sx={{ height: buttonBarHeight, paddingTop: 0.5 }}>
        <Grid container>
          <Grid item xs={6} sx={{ padding: 1 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => props.onCancel()}
            >
              {props.translations.cancel}
            </Button>
          </Grid>
          <Grid
            item
            xs={6}
            sx={{ padding: 1 }}
            onClick={() => props.onChangeConfirmed(selectedMaterialSku)}
          >
            <Button variant="contained" color="primary" fullWidth>
              {props.translations.continue}
            </Button>
          </Grid>
        </Grid>
      </PricingFooter>
    </>
  );
};
